import React, { useState } from 'react';
import api from '../../utils/api';
import Modal from '../../components/common/Modal';
import { useToast } from '../../hooks/useToast';
import { isValidPhoneNumber } from '../../utils/validators';
import { formatPhoneNumber } from '../../utils/helpers';

const initialForm = {
	fullName: '',
	email: '',
	phoneNumber: '',
	gender: '',
	ageRange: '',
	organizationName: '',
	roleTitle: '',
	industrySector: '',
	county: '',
	questions: '',
	consentUpdates: false,
	consentMedia: false,
};

const BootcampRegistration = () => {
	const [form, setForm] = useState(initialForm);
	const [loading, setLoading] = useState(false);
	const [reg, setReg] = useState(null);
	const [paymentOpen, setPaymentOpen] = useState(false);
	const [payPhone, setPayPhone] = useState('');
	const [stkStatus, setStkStatus] = useState(null);
	const [paymentStatus, setPaymentStatus] = useState(null);
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [paymentDetails, setPaymentDetails] = useState(null);
	const [errors, setErrors] = useState({});
	const { showSuccess, showError } = useToast();

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;
		setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
		
		// Clear error when user starts typing
		if (errors[name]) {
			setErrors(prev => ({ ...prev, [name]: '' }));
		}
		
		// Auto-format phone number as user types
		if (name === 'phoneNumber' && value) {
			// Basic formatting - remove any non-digit characters except + and 0 at start
			const cleaned = value.replace(/[^\d+]/g, '');
			if (cleaned !== value) {
				setForm((prev) => ({ ...prev, [name]: cleaned }));
			}
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		
		// Validate form
		const newErrors = {};
		
		// Phone number validation
		if (!form.phoneNumber) {
			newErrors.phoneNumber = 'Phone number is required';
		} else if (!isValidPhoneNumber(form.phoneNumber)) {
			newErrors.phoneNumber = 'Please enter a valid Kenyan phone number (e.g., +254712345678 or 0712345678)';
		}
		
		// Basic email validation
		if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
			newErrors.email = 'Please enter a valid email address';
		}
		
		// Required field validation
		if (!form.fullName) newErrors.fullName = 'Full name is required';
		if (!form.organizationName) newErrors.organizationName = 'Organization name is required';
		if (!form.roleTitle) newErrors.roleTitle = 'Role/job title is required';
		if (!form.industrySector) newErrors.industrySector = 'Industry/sector is required';
		if (!form.county) newErrors.county = 'County is required';
		
		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			showError('Please fix the form errors before submitting');
			return;
		}
		
		setLoading(true);
		try {
			// Format phone number before sending
			const formattedData = {
				...form,
				phoneNumber: formatPhoneNumber(form.phoneNumber),
			};
			
			const resp = await api.post('/events/register', {
				...formattedData,
				paymentAmount: 3500,
			});
			setReg(resp.data.data);
			setPaymentOpen(true);
			showSuccess('Registration received. Proceed to payment.');
		} catch (err) {
			showError(err.details?.error || 'Failed to submit');
		} finally {
			setLoading(false);
		}
	};

	const initiateSTK = async () => {
		if (!reg) return;
		
		// Validate payment phone number
		const phoneToUse = payPhone || reg.phoneNumber;
		if (!phoneToUse) {
			showError('Phone number is required for payment');
			return;
		}
		
		if (!isValidPhoneNumber(phoneToUse)) {
			showError('Please enter a valid Kenyan phone number (e.g., +254712345678 or 0712345678)');
			return;
		}
		
		try {
			setStkStatus('sending');
			const r = await api.post(`/events/registrations/${reg._id}/mpesa`, {
				phoneNumber: formatPhoneNumber(phoneToUse),
				amount: reg.payment?.amount || 3500,
			});
			setStkStatus('sent');
			showSuccess(r.data.data?.customerMessage || 'STK prompt sent');
			
			// Start polling for payment status
			startPaymentStatusPolling(reg._id);
		} catch (err) {
			setStkStatus('error');
			showError(err.details?.error || 'Failed to send STK');
		}
	};

	const startPaymentStatusPolling = (registrationId) => {
		let attempts = 0;
		const maxAttempts = 20; // Poll for up to ~2 minutes
		
		const checkStatus = async () => {
			if (attempts >= maxAttempts) {
				setStkStatus('timeout');
				showError('Payment timed out. Please try again if payment was not completed.');
				return;
			}

			try {
				const response = await api.get(`/events/registrations/${registrationId}`);
				const registration = response.data.data;
				
				if (registration.status === 'paid') {
					setStkStatus('completed');
					setPaymentStatus('completed');
					setPaymentDetails({
						receipt: registration.payment?.mpesa?.mpesaReceiptNumber || 'N/A',
						amount: registration.payment?.amount || 3500,
						name: registration.fullName
					});
					setPaymentOpen(false);
					setShowSuccessModal(true);
					return;
				} else if (registration.status === 'failed') {
					setStkStatus('failed');
					setPaymentStatus('failed');
					showError('Payment failed. Please try again.');
					return;
				}
				
				// Continue polling if still pending
				attempts++;
				setTimeout(checkStatus, 6000); // Check every 6 seconds
			} catch (error) {
				console.error('Error checking payment status:', error);
				attempts++;
				setTimeout(checkStatus, 6000);
			}
		};

		setTimeout(checkStatus, 5000); // Start checking after 5 seconds
	};

	return (
		<div className="container mx-auto px-4 py-8 max-w-3xl">
			<h1 className="text-2xl font-bold mb-4">SME Digital Transformation Bootcamp Registration</h1>
			<form onSubmit={handleSubmit} className="bg-white shadow-soft rounded p-6 space-y-4">
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div>
						<input 
							name="fullName" 
							placeholder="Full Name" 
							className={`form-input ${errors.fullName ? 'border-red-500' : ''}`} 
							required 
							value={form.fullName} 
							onChange={handleChange} 
						/>
						{errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
					</div>
					<div>
						<input 
							name="email" 
							type="email" 
							placeholder="Email Address" 
							className={`form-input ${errors.email ? 'border-red-500' : ''}`} 
							required 
							value={form.email} 
							onChange={handleChange} 
						/>
						{errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
					</div>
					<div className="md:col-span-2">
						<input 
							name="phoneNumber" 
							placeholder="Phone Number (e.g., +254712345678 or 0712345678)" 
							className={`form-input ${errors.phoneNumber ? 'border-red-500' : ''}`} 
							required 
							value={form.phoneNumber} 
							onChange={handleChange} 
						/>
						{errors.phoneNumber && <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>}
					</div>
					<select name="gender" className="form-select" value={form.gender} onChange={handleChange}>
						<option value="">Gender (optional)</option>
						<option>Male</option>
						<option>Female</option>
						<option>Other</option>
					</select>
					<select name="ageRange" className="form-select" value={form.ageRange} onChange={handleChange}>
						<option value="">Age / Age Range</option>
						<option>18 - 25</option>
						<option>26 - 35</option>
						<option>36 - 45</option>
						<option>46 - 55</option>
						<option>56 +</option>
					</select>
					<div>
						<input 
							name="organizationName" 
							placeholder="Organization / Business Name" 
							className={`form-input ${errors.organizationName ? 'border-red-500' : ''}`} 
							required 
							value={form.organizationName} 
							onChange={handleChange} 
						/>
						{errors.organizationName && <p className="text-red-500 text-sm mt-1">{errors.organizationName}</p>}
					</div>
					<div>
						<input 
							name="roleTitle" 
							placeholder="Role / Job Title" 
							className={`form-input ${errors.roleTitle ? 'border-red-500' : ''}`} 
							required 
							value={form.roleTitle} 
							onChange={handleChange} 
						/>
						{errors.roleTitle && <p className="text-red-500 text-sm mt-1">{errors.roleTitle}</p>}
					</div>
					<div>
						<select 
							name="industrySector" 
							className={`form-select ${errors.industrySector ? 'border-red-500' : ''}`} 
							required 
							value={form.industrySector} 
							onChange={handleChange}
						>
							<option value="">Industry / Sector</option>
							{['Education','Technology','Business','Healthcare / Health','Finance / Banking','Retail / E-commerce','Government / Public Sector','Nonprofit / NGO','Manufacturing','Construction','Legal / Law','Real Estate','Transportation / Logistics','Hospitality / Tourism','Media / Entertainment','Telecommunications','Agriculture','Energy / Utilities','Insurance','Automotive','Food & Beverage','Consulting','Environmental Services','Other'].map((s) => (
								<option key={s} value={s}>{s}</option>
							))}
						</select>
						{errors.industrySector && <p className="text-red-500 text-sm mt-1">{errors.industrySector}</p>}
					</div>
					<div>
						<input 
							name="county" 
							placeholder="Your County" 
							className={`form-input ${errors.county ? 'border-red-500' : ''}`} 
							required 
							value={form.county} 
							onChange={handleChange} 
						/>
						{errors.county && <p className="text-red-500 text-sm mt-1">{errors.county}</p>}
					</div>
					<textarea name="questions" placeholder="Any expectations or questions?" className="form-textarea md:col-span-2" value={form.questions} onChange={handleChange} />
					<label className="flex items-center space-x-2 md:col-span-2">
						<input type="checkbox" name="consentUpdates" checked={form.consentUpdates} onChange={handleChange} />
						<span>Agree to receive event updates via WhatsApp/Email</span>
					</label>
					<label className="flex items-center space-x-2 md:col-span-2">
						<input type="checkbox" name="consentMedia" checked={form.consentMedia} onChange={handleChange} />
						<span>Consent for photography/video use</span>
					</label>
				</div>
				<button className="btn-primary" disabled={loading}>{loading ? 'Submitting...' : 'Submit'}</button>
			</form>

			<Modal isOpen={paymentOpen} onClose={() => setPaymentOpen(false)} title="Complete Payment">
				<div className="space-y-3">
					<p>Enter your phone number to receive an M-Pesa STK prompt.</p>
					<div>
						<input 
							className="form-input" 
							placeholder="2547XXXXXXXX or 0712345678" 
							value={payPhone} 
							onChange={(e) => {
								const value = e.target.value.replace(/[^\d+]/g, ''); // Only allow digits and +
								setPayPhone(value);
							}}
						/>
						<p className="text-xs text-gray-500 mt-1">
							Use a different number or leave empty to use your registration number: {formatPhoneNumber(reg?.phoneNumber || '')}
						</p>
					</div>
					<button className="btn-primary" onClick={initiateSTK} disabled={stkStatus==='sending' || stkStatus==='sent'}>
						{stkStatus === 'sending' ? 'Sending...' : stkStatus === 'sent' ? 'Waiting for payment...' : 'Send STK Prompt'}
					</button>
					<div className="text-sm text-gray-600">
						<span>Prefer manual payment?</span>
						<div>Till Number: 8267352 | Name: MEIRA AFRICA | Amount: 3500 KES</div>
						<div>After payment, you will get a receipt from info@meira.co.ke</div>
					</div>
					{stkStatus === 'error' && (
						<button className="btn-secondary" onClick={initiateSTK}>Retry STK Prompt</button>
					)}
					{stkStatus === 'sent' && (
						<div className="text-blue-600 text-sm">
							<p>📱 Check your phone for the M-Pesa prompt and enter your PIN to complete payment.</p>
							<p>This may take a few seconds to appear.</p>
						</div>
					)}
				</div>
			</Modal>

			<Modal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} title="Payment Successful! 🎉">
				<div className="space-y-4">
					<div className="text-center">
						<div className="text-green-600 text-6xl mb-4">✅</div>
						<h3 className="text-xl font-semibold text-gray-900 mb-2">
							Payment Received!
						</h3>
						<p className="text-gray-600">
							Thank you {paymentDetails?.name}! Your registration is now complete.
						</p>
					</div>
					
					<div className="bg-gray-50 p-4 rounded-lg space-y-2">
						<div className="flex justify-between">
							<span className="text-gray-600">Amount Paid:</span>
							<span className="font-medium">KES {paymentDetails?.amount}</span>
						</div>
						<div className="flex justify-between">
							<span className="text-gray-600">Receipt Number:</span>
							<span className="font-medium">{paymentDetails?.receipt}</span>
						</div>
					</div>
					
					<div className="text-sm text-gray-600 space-y-2">
						<p>📧 You will receive event details and updates via email.</p>
						<p>📱 We'll also send you updates via WhatsApp if you provided consent.</p>
						<p>❓ If you have any questions, please contact us at info@meira.co.ke</p>
					</div>
					
					<button 
						className="w-full btn-primary" 
						onClick={() => {
							setShowSuccessModal(false);
							// Reset form for potential new registration
							setForm(initialForm);
							setReg(null);
							setPaymentOpen(false);
							setStkStatus(null);
							setPaymentStatus(null);
							setPaymentDetails(null);
						}}
					>
						Close
					</button>
				</div>
			</Modal>
		</div>
	);
};

export default BootcampRegistration;


